/**
 * RallyStream appliance list + operator registration form.
 *
 * Visual shell uses RallyStream palette (see docs/RALLYSTREAM_FLEET_INTEGRATION.md).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import {
  createRallystreamDevice,
  listCustomers,
  listRallystreamDevices,
} from "@/lib/api";
import { formatDateTime, formatRelative } from "@/lib/format";
import type { Customer, RallyStreamDeviceCreateInputs } from "@/lib/types";

const RS_SURFACE = "min-h-full bg-[#0F0E0C] text-slate-100";
const RS_PANEL =
  "rounded-lg border border-white/10 bg-black/30 shadow-sm shadow-black/40";
const RS_INPUT =
  "mt-1 w-full rounded-md border border-white/15 bg-[#151412] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#FB6E2E] focus:outline-none focus:ring-1 focus:ring-[#FB6E2E]/40";
const RS_BTN_PRIMARY =
  "inline-flex items-center justify-center rounded-md bg-[#FB6E2E] px-4 py-2 text-sm font-semibold text-[#0F0E0C] hover:bg-[#ff7d42] disabled:opacity-50";

function isValidPublicId(s: string): boolean {
  return /^[0-9a-f]{32}$/i.test(s.trim());
}

function isLikelyBase64(s: string): boolean {
  return /^[A-Za-z0-9+/]+=*$/.test(s.trim()) && s.trim().length >= 4;
}

export default function StreamDevices() {
  const qc = useQueryClient();
  const [form, setForm] = useState<RallyStreamDeviceCreateInputs>({
    device_public_id: "",
    ed25519_pubkey_b64: "",
    friendly_label: "",
    customer_id: "",
    notes: "",
  });

  const devicesQ = useQuery({
    queryKey: ["rallystream-devices"],
    queryFn: listRallystreamDevices,
  });

  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: listCustomers,
  });

  const createM = useMutation({
    mutationFn: createRallystreamDevice,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["rallystream-devices"] });
      setForm({
        device_public_id: "",
        ed25519_pubkey_b64: "",
        friendly_label: "",
        customer_id: "",
        notes: "",
      });
    },
  });

  const formValid = useMemo(() => {
    const pid = form.device_public_id.trim().toLowerCase();
    const pk = form.ed25519_pubkey_b64.trim();
    return isValidPublicId(pid) && isLikelyBase64(pk);
  }, [form.device_public_id, form.ed25519_pubkey_b64]);

  function submitRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!formValid || createM.isPending) return;
    const payload: RallyStreamDeviceCreateInputs = {
      device_public_id: form.device_public_id.trim().toLowerCase(),
      ed25519_pubkey_b64: form.ed25519_pubkey_b64.trim(),
      friendly_label: form.friendly_label?.trim() || null,
      customer_id: form.customer_id?.trim() || null,
      notes: form.notes?.trim() || null,
    };
    createM.mutate(payload);
  }

  return (
    <div className={RS_SURFACE}>
      <div className="mx-auto max-w-6xl px-8 py-8 space-y-8">
        <PageHeader
          title="RallyStream"
          subtitle="Live encoder appliances registered in the fleet database."
        />

        <section className={`${RS_PANEL} p-6 space-y-4`}>
          <h2 className="text-sm font-semibold uppercase tracking-widest text-[#FB6E2E]">
            Register appliance
          </h2>
          <p className="text-sm text-slate-400">
            Factory provisioning will replace this form over time. For now,
            paste the 32-character hex device id and the base64-encoded Ed25519
            public key.
          </p>
          <form className="grid gap-4 md:grid-cols-2" onSubmit={submitRegister}>
            <label className="block text-xs uppercase tracking-wide text-slate-400">
              Device public id (32 hex)
              <input
                className={RS_INPUT}
                value={form.device_public_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, device_public_id: e.target.value }))
                }
                placeholder="e.g. first 16 bytes of sha256(pubkey) as hex"
                spellCheck={false}
              />
            </label>
            <label className="block text-xs uppercase tracking-wide text-slate-400">
              Ed25519 public key (base64)
              <input
                className={RS_INPUT}
                value={form.ed25519_pubkey_b64}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ed25519_pubkey_b64: e.target.value }))
                }
                spellCheck={false}
              />
            </label>
            <label className="block text-xs uppercase tracking-wide text-slate-400">
              Friendly label
              <input
                className={RS_INPUT}
                value={form.friendly_label ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, friendly_label: e.target.value }))
                }
              />
            </label>
            <label className="block text-xs uppercase tracking-wide text-slate-400">
              Customer (optional)
              <select
                className={RS_INPUT}
                value={form.customer_id ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, customer_id: e.target.value }))
                }
              >
                <option value="">— none —</option>
                {(customersQ.data ?? []).map((c: Customer) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs uppercase tracking-wide text-slate-400 md:col-span-2">
              Notes
              <input
                className={RS_INPUT}
                value={form.notes ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, notes: e.target.value }))
                }
              />
            </label>
            <div className="md:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                className={RS_BTN_PRIMARY}
                disabled={!formValid || createM.isPending}
              >
                {createM.isPending ? "Saving…" : "Register"}
              </button>
              {createM.isError ? (
                <span className="text-sm text-rose-400">
                  {(createM.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </section>

        <section className={`${RS_PANEL} p-0 overflow-hidden`}>
          <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-[#3EC97A]">
              Appliances
            </h2>
            <span className="text-xs text-slate-500">
              {devicesQ.data?.length ?? "…"} total
            </span>
          </div>
          {devicesQ.isLoading ? <LoadingBlock label="Loading appliances" /> : null}
          {devicesQ.isError ? (
            <div className="p-6">
              <ErrorBlock error={devicesQ.error} />
            </div>
          ) : null}
          {devicesQ.isSuccess && devicesQ.data.length === 0 ? (
            <div className="p-6">
              <EmptyBlock
                title="No appliances yet"
                description="Register a unit above, or apply migration 0011 and seed data."
              />
            </div>
          ) : null}
          {devicesQ.isSuccess && devicesQ.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-black/40 text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-4 py-3">Public id</th>
                    <th className="px-4 py-3">Label</th>
                    <th className="px-4 py-3">Firmware target</th>
                    <th className="px-4 py-3">Last check-in</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {devicesQ.data.map((d) => (
                    <tr key={d.id} className="hover:bg-white/5">
                      <td className="px-4 py-3 font-mono text-xs text-[#4A8FE5]">
                        {d.device_public_id}
                      </td>
                      <td className="px-4 py-3 text-slate-200">
                        {d.friendly_label ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {d.firmware_target}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {d.last_check_in_at ? (
                          <span
                            title={formatDateTime(d.last_check_in_at)}
                            className="cursor-help"
                          >
                            {formatRelative(d.last_check_in_at)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/stream/devices/${encodeURIComponent(d.device_public_id)}`}
                          className="text-[#FB6E2E] hover:underline text-xs font-semibold uppercase tracking-wide"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
